import { z } from 'zod';
import { openWorldCommandIdSchema } from './crossing';
import { openWorldDirectionSchema } from './direction';

/**
 * **A faixa de fronteira, a proteção que ela dá, e a canalização.**
 *
 * ## O problema que a faixa resolve
 *
 * Um quadrante perigoso não tem saída segura. Quem chega à borda para viajar,
 * para entrar num porto ou para preparar uma extração fica parado exatamente
 * onde toda a gente sabe que ele vai estar — e sem nada que o proteja, a borda
 * seria o sítio onde se espera por gente indefesa em vez do sítio por onde se
 * sai.
 *
 * A resposta é uma janela curta e **irrepetível**: vinte segundos em que os
 * tiros não aterram, seguidos de três minutos em que ela não volta. Chega para
 * atravessar, chega para canalizar dez segundos, e não chega para viver na
 * fronteira.
 *
 * ## A proteção NÃO é o Bastião
 *
 * O Bastião é uma carta, é **só de PvP**, e não existe na sala do mundo aberto.
 * Esta proteção não a consome, não a bloqueia, não lhe mexe no tempo de espera
 * e não lhe muda o nível. São duas coisas com dois donos.
 *
 * E se um dia as duas puderem estar ativas ao mesmo tempo, **as durações não se
 * somam**: estar protegido é uma disjunção — protegido por uma OU pela outra —
 * e cada uma acaba no seu próprio instante. Somar seria transformar duas
 * defesas independentes numa terceira que ninguém desenhou.
 */

/* -------------------------------------------------------------------------- */
/*  Os relógios                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Quanto tempo a proteção de fronteira dura: **vinte segundos**.
 *
 * O dobro da canalização, e é por isso que é este número: uma canalização de
 * dez segundos cabe inteira dentro dela com margem para chegar à faixa e para
 * decidir. Menos do que isso e a proteção não chegaria para a coisa que ela
 * existe para permitir.
 */
export const OPEN_WORLD_BORDER_PROTECTION_MS = 20_000;

/**
 * Quanto tempo a proteção demora a voltar: **três minutos**.
 *
 * Nove vezes a sua própria duração. É o que faz dela uma saída e não um estado:
 * um piloto que a gastou a entrar na faixa e voltou a sair não a tem outra vez,
 * e a faixa deixa de ser um abrigo para onde se corre sempre que a luta corre
 * mal.
 *
 * O relógio começa **quando ela é concedida**, não quando acaba — portanto os
 * três minutos incluem os vinte segundos, e a espera real entre duas proteções
 * é de dois minutos e quarenta segundos.
 */
export const OPEN_WORLD_BORDER_PROTECTION_COOLDOWN_MS = 3 * 60_000;

/**
 * Quanto tempo uma canalização leva: **dez segundos**.
 *
 * O mesmo para entrar num porto e para preparar uma extração, porque as duas
 * são a mesma coisa do ponto de vista de quem está a ver: alguém parado na
 * borda, a fazer algo que lhe vai dar vantagem, e que pode ser impedido.
 *
 * Uma viagem comum entre dois quadrantes perigosos **não** passa por aqui. Ela
 * continua a ser o handoff de duas fases do V28I.C e continua a não custar
 * segundos: atravessar é ordinário, e sair do perigo não é.
 */
export const OPEN_WORLD_CHANNEL_MS = 10_000;

/** Quanto tempo uma nave tem de ficar quieta num porto antes de o reparo começar. */
export const OPEN_WORLD_REPAIR_IDLE_MS = 5_000;

/**
 * Em quantos segundos um casco vazio enche: **vinte**.
 *
 * A taxa é `vida máxima / 20 s`, portanto a duração é a mesma para toda a gente
 * — uma nave de dez mil demora o mesmo que uma de cem, e cada uma enche o seu
 * próprio depósito. Uma taxa fixa em pontos por segundo faria o contrário: uma
 * nave grande esperaria proporcionalmente mais por ser grande.
 */
export const OPEN_WORLD_REPAIR_SECONDS = 20;

/* -------------------------------------------------------------------------- */
/*  A canalização                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Para que serve uma canalização. Duas finalidades, e nada mais.
 *
 * - `port` — entrar no porto vizinho. Termina num handoff autoritativo.
 * - `extraction` — preparar uma extração. **Neste gate ela só chega a
 *   `extraction_ready`**: nada é transferido, nada é vendido e nada volta à
 *   caça, porque nada disso está decidido.
 */
export const OPEN_WORLD_CHANNEL_PURPOSES = ['port', 'extraction'] as const;
export type OpenWorldChannelPurpose = (typeof OPEN_WORLD_CHANNEL_PURPOSES)[number];

/**
 * Como uma canalização acabou. Uma lista fechada.
 *
 * `completed` é o único desfecho bom, e o que ele significa depende da
 * finalidade: uma canalização de porto **começa** o handoff, e uma de extração
 * responde `extraction_ready` e para ali.
 */
export const OPEN_WORLD_CHANNEL_ENDINGS = [
  'completed',
  /** O jogador cancelou. */
  'cancelled',
  /** Mexeu-se. Movimento é a coisa que uma canalização proíbe. */
  'moved',
  /** Levou dano positivo sem proteção. Com proteção isto não acontece. */
  'damaged',
  /** Morreu. */
  'destroyed',
  /** Saiu da faixa. */
  'left_border',
  /** A ligação caiu, ou outra tomou a cadeira. */
  'disconnected',
  /** O quadrante deixou de ter um porto vizinho naquela direção. */
  'no_destination',
] as const;
export type OpenWorldChannelEnding = (typeof OPEN_WORLD_CHANNEL_ENDINGS)[number];

/** Porque uma ordem de fronteira foi recusada. Fechada, e cada uma é um facto. */
export const OPEN_WORLD_BORDER_REFUSALS = [
  /** O casco não está inteiramente dentro da faixa. */
  'not_in_strip',
  /** Já há uma canalização em curso nesta cadeira. */
  'already_channelling',
  /** O casco está destruído. */
  'destroyed',
  /** Não há porto naquela direção. */
  'no_port',
  /** Já há uma travessia em voo. Uma de cada vez. */
  'already_crossing',
  /** Está num porto. Um porto não tem faixa de fronteira. */
  'in_port',
] as const;
export type OpenWorldBorderRefusal = (typeof OPEN_WORLD_BORDER_REFUSALS)[number];

/* -------------------------------------------------------------------------- */
/*  O que o cliente pode dizer                                                */
/* -------------------------------------------------------------------------- */

/**
 * **Entrar no porto vizinho.**
 *
 * O cliente diz uma direção e a sua própria ordem. **Não diz que canalizou**,
 * não diz quanto tempo passou e não diz que terminou: quem conta os dez
 * segundos é o servidor, no seu relógio, e não há campo nenhum onde uma
 * conclusão pudesse ser declarada.
 */
export const openWorldPortEntrySchema = z
  .object({
    t: z.literal('bp'),
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    id: openWorldCommandIdSchema,
    /** Qual vizinho. O servidor verifica que é um porto; o cliente não o afirma. */
    d: openWorldDirectionSchema,
  })
  .strict();

/**
 * **Preparar uma extração.**
 *
 * Sem direção, porque não se vai a lado nenhum. Neste gate o desfecho
 * autoritativo é `extraction_ready` e mais nada: nenhum bem é transferido,
 * nenhum peixe é vendido e ninguém volta à caça.
 */
export const openWorldExtractionStartSchema = z
  .object({
    t: z.literal('be'),
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    id: openWorldCommandIdSchema,
  })
  .strict();

/** **Cancelar.** Sem id de ordem: cancelar não decide nada e repetir é o mesmo. */
export const openWorldBorderCancelSchema = z
  .object({
    t: z.literal('bx'),
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  })
  .strict();

/* -------------------------------------------------------------------------- */
/*  O que o servidor responde                                                 */
/* -------------------------------------------------------------------------- */

/**
 * **O estado de fronteira desta cadeira**, dito só a ela.
 *
 * Não vai na área de interesse e não descreve mais ninguém: saber que o
 * adversário está protegido é uma coisa que se descobre a disparar contra ele.
 *
 * Os três relógios chegam em **milissegundos restantes**, calculados pelo
 * servidor no instante em que a trama sai. Enviar um instante absoluto obrigaria
 * o cliente a ter o mesmo relógio que o servidor, e ele não tem.
 */
/**
 * **Uma saída que esta borda oferece AGORA**, dita pela autoridade.
 *
 * O cliente não deriva vizinhos. Ele não sabe onde acaba a grelha, não sabe
 * quais dos cem quadrantes são portos e não sabe se o casco satisfaz a banda
 * naquela direção — e cada uma dessas três coisas, adivinhada, produziria um
 * botão que oferece uma viagem que a sala depois recusa.
 *
 * Por isso viaja a **coordenada** e não só a direção: um botão que diz
 * `SUL — B/3` é uma promessa verificável, e `SUL` sozinho é uma seta.
 */
export const openWorldBorderOptionSchema = z
  .object({
    /** Por onde se sai. */
    d: openWorldDirectionSchema,
    /** Para onde se vai. Linha e coluna, como a grelha as conta. */
    r: z.number().int().min(0),
    c: z.number().int().min(0),
    /** É um porto seguro do outro lado. */
    p: z.boolean(),
    /**
     * Custa canalização.
     *
     * Hoje é exatamente "entrar num porto": sair de um é livre e viajar entre
     * quadrantes perigosos é imediato. Viaja como um facto e não como uma
     * regra, para o cliente rotular sem reconstruir a decisão.
     */
    ch: z.boolean(),
  })
  .strict();

export type OpenWorldBorderOption = z.infer<typeof openWorldBorderOptionSchema>;

export const openWorldBorderStateSchema = z
  .object({
    t: z.literal('bs'),
    /** Está o casco INTEIRO dentro da faixa. */
    s: z.boolean(),
    /** Quanto falta de proteção. Zero quando não há. */
    p: z.number().int().min(0),
    /** Quanto falta para a proteção voltar a estar disponível. Zero quando já está. */
    cd: z.number().int().min(0),
    /** A finalidade da canalização em curso, ou null. */
    c: z.enum(OPEN_WORLD_CHANNEL_PURPOSES).nullable(),
    /** Quanto falta da canalização, em milissegundos. Zero quando não há nenhuma. */
    cr: z.number().int().min(0),
    /** Está esta sala num porto seguro. */
    safe: z.boolean(),
    /**
     * **As saídas que existem daqui**, e só elas.
     *
     * Vazio quando o casco está encostado a um limite externo do mundo: uma
     * borda sem vizinho é uma parede, e uma parede não se oferece.
     */
    o: z.array(openWorldBorderOptionSchema).max(4),
  })
  .strict();

export type OpenWorldBorderState = z.infer<typeof openWorldBorderStateSchema>;

/**
 * **Como uma canalização acabou**, dito à ligação que a pediu.
 *
 * `extraction_ready` viaja aqui e em nenhum outro sítio: é o único desfecho que
 * uma extração pode ter neste gate, e é uma resposta e não uma transferência.
 */
export const openWorldChannelResultSchema = z
  .object({
    t: z.literal('bc'),
    id: openWorldCommandIdSchema,
    p: z.enum(OPEN_WORLD_CHANNEL_PURPOSES),
    e: z.enum(OPEN_WORLD_CHANNEL_ENDINGS),
    /**
     * **A extração está pronta**, e é tudo o que este gate diz sobre ela.
     *
     * Verdadeiro apenas quando uma canalização de extração chegou ao fim. Nada
     * foi movido, nada foi vendido e nada voltou à caça: o gate que decidir o
     * que uma extração faz é que há de ter para onde levar isto.
     */
    ready: z.boolean(),
  })
  .strict();

export type OpenWorldChannelResult = z.infer<typeof openWorldChannelResultSchema>;

/** Uma ordem de fronteira recusada. Nada aconteceu ao casco. */
export const openWorldBorderRefusalSchema = z
  .object({
    t: z.literal('bf'),
    id: openWorldCommandIdSchema,
    r: z.enum(OPEN_WORLD_BORDER_REFUSALS),
  })
  .strict();
